import { Wind } from "lucide-react";

interface WindCompassProps {
  speed: number;
  deg: number;
  gust?: number;
}

function dirLabel(deg: number): string {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

export function WindCompass({ speed, deg, gust }: WindCompassProps) {
  const cx = 60;
  const cy = 60;
  const r = 45;

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Wind className="h-4 w-4 text-primary" />
        Wind
      </h3>
      <div className="flex items-center gap-4">
        <svg viewBox="0 0 120 120" className="w-24 h-24 flex-shrink-0">
          {/* Outer ring */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="oklch(1 0 0 / 0.08)" strokeWidth="1" />
          <circle cx={cx} cy={cy} r={r - 5} fill="oklch(1 0 0 / 0.02)" />

          {/* Cardinal markers */}
          {["N", "E", "S", "W"].map((dir, i) => {
            const a = (i * 90 - 90) * Math.PI / 180;
            const x = cx + (r - 2) * Math.cos(a);
            const y = cy + (r - 2) * Math.sin(a);
            return (
              <text
                key={dir}
                x={x}
                y={y + 3}
                textAnchor="middle"
                className="text-[9px] fill-muted-foreground font-semibold"
              >
                {dir}
              </text>
            );
          })}

          {/* Tick marks */}
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i * 15 - 90) * Math.PI / 180;
            const x1 = cx + (r - 8) * Math.cos(a);
            const y1 = cy + (r - 8) * Math.sin(a);
            const x2 = cx + (r - 12) * Math.cos(a);
            const y2 = cy + (r - 12) * Math.sin(a);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="oklch(1 0 0 / 0.15)" strokeWidth="1" />;
          })}

          {/* Wind arrow */}
          <g transform={`rotate(${deg} ${cx} ${cy})`}>
            <defs>
              <linearGradient id="windArrow" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="oklch(0.78 0.16 195)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="oklch(0.78 0.16 195)" stopOpacity="1" />
              </linearGradient>
            </defs>
            <path
              d={`M ${cx} ${cy - 30} L ${cx - 6} ${cy + 8} L ${cx} ${cy + 4} L ${cx + 6} ${cy + 8} Z`}
              fill="url(#windArrow)"
            />
          </g>

          {/* Center dot */}
          <circle cx={cx} cy={cy} r="3" fill="oklch(0.78 0.16 195)" />
        </svg>

        <div className="space-y-1">
          <div>
            <p className="text-2xl font-bold">{speed.toFixed(1)}<span className="text-sm font-normal text-muted-foreground"> m/s</span></p>
            <p className="text-xs text-muted-foreground">{dirLabel(deg)} · {Math.round(deg)}°</p>
          </div>
          {gust !== undefined && gust > 0 && (
            <p className="text-xs text-amber-400">Gusts {gust.toFixed(1)} m/s</p>
          )}
        </div>
      </div>
    </div>
  );
}
